import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import BrandLogo from '../components/BrandLogo';
import { useAuth } from '../context/AuthContext';
import {
  AnimatedText,
  EASE_OUT,
  fadeUpVariants,
  pageVariants,
  reducedVariants,
} from '../motion';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

const DIGITS = ['4', '0', '4'];

function LostSignal({ reduced }) {
  if (reduced) {
    return (
      <svg viewBox="0 0 120 40" className="h-10 w-[7.5rem]" fill="none" aria-hidden="true">
        <polyline
          points="0,20 18,20 26,8 34,32 42,20 58,20 66,6 74,34 82,20 100,20 120,20"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-teal-600/70"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 120 40" className="h-10 w-[7.5rem]" fill="none" aria-hidden="true">
      <motion.polyline
        points="0,20 18,20 26,8 34,32 42,20 58,20 66,6 74,34 82,20 100,20 120,20"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-teal-600"
        initial={{ pathLength: 0, opacity: 0.25 }}
        animate={{
          pathLength: [0, 1, 0.62],
          opacity: [0.25, 1, 0.55],
        }}
        transition={{ duration: 1.45, ease: EASE_OUT, times: [0, 0.55, 1] }}
      />
      <motion.circle
        cx="100"
        cy="20"
        r="3.2"
        className="fill-teal-500"
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: [0, 1, 0.35, 1], scale: [0.4, 1, 0.85, 1] }}
        transition={{ duration: 1.6, ease: EASE_OUT, delay: 0.55 }}
      />
    </svg>
  );
}

export default function NotFoundPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const reduced = usePrefersReducedMotion();
  const variants = reducedVariants(pageVariants, reduced);
  const item = reducedVariants(fadeUpVariants, reduced);
  const homeTo = isAuthenticated === true ? '/mydashboard' : '/';
  const pathLabel = location.pathname || '/';

  return (
    <div className="vitra-atmosphere relative flex min-h-screen flex-col overflow-hidden">
      <motion.div
        className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-teal-400/20 blur-3xl"
        aria-hidden="true"
        animate={reduced ? undefined : { x: [0, 18, 0], y: [0, 12, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute -right-16 bottom-10 h-80 w-80 rounded-full bg-teal-600/10 blur-3xl"
        aria-hidden="true"
        animate={reduced ? undefined : { x: [0, -14, 0], y: [0, -10, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />

      <header className="relative z-[1] flex items-center justify-between px-5 py-5 sm:px-8 lg:px-12">
        <BrandLogo to={homeTo} markClassName="h-8 w-8 sm:h-9 sm:w-9" wordmarkClassName="h-4 sm:h-5" />
        <motion.p
          className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-800/55"
          initial={reduced ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: EASE_OUT, delay: 0.1 }}
        >
          Page not found
        </motion.p>
      </header>

      <motion.main
        className="relative z-[1] flex flex-1 flex-col items-center justify-center px-5 pb-16 pt-6 text-center sm:px-8"
        variants={variants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={item} className="mb-6 flex flex-col items-center gap-3">
          <div className="inline-flex items-center justify-center rounded-full border border-teal-200/80 bg-white/70 px-5 py-3 text-teal-700 shadow-[0_10px_40px_-18px_rgba(13,148,136,0.55)] backdrop-blur-sm">
            <LostSignal reduced={reduced} />
          </div>
        </motion.div>

        <motion.div
          variants={item}
          className="mb-3 flex items-baseline justify-center gap-1 sm:gap-2"
          aria-hidden="true"
        >
          {DIGITS.map((digit, i) => (
            <motion.span
              key={`${digit}-${i}`}
              className="bg-gradient-to-b from-teal-700 to-teal-900 bg-clip-text text-[5.5rem] font-bold leading-none tracking-tight text-transparent sm:text-[7.5rem] lg:text-[8.75rem]"
              initial={reduced ? false : { opacity: 0, y: 28, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.55, ease: EASE_OUT, delay: 0.08 + i * 0.08 }}
            >
              {digit}
            </motion.span>
          ))}
        </motion.div>

        <h1 className="sr-only">404 — Page not found</h1>

        <motion.div variants={item} className="max-w-xl">
          <AnimatedText
            text="This path isn’t on the map"
            as="p"
            className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl"
            delay={0.18}
          />
          <motion.p
            className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base"
            initial={reduced ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.42 }}
          >
            The page you’re looking for doesn’t exist, was moved, or the URL was mistyped.
          </motion.p>
        </motion.div>

        <motion.div
          variants={item}
          className="mt-6 inline-flex max-w-full items-center gap-2 rounded-full border border-teal-200/70 bg-white/75 px-3.5 py-2 text-left shadow-sm backdrop-blur-sm"
        >
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-700/70">
            Path
          </span>
          <code className="truncate font-mono text-xs text-slate-700 sm:text-sm" title={pathLabel}>
            {pathLabel}
          </code>
        </motion.div>

        <motion.div
          variants={item}
          className="mt-8 flex w-full max-w-md flex-col items-stretch gap-3 sm:flex-row sm:justify-center"
        >
          <Link
            to={homeTo}
            className="inline-flex items-center justify-center rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_-14px_rgba(13,148,136,0.9)] transition hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
          >
            {isAuthenticated === true ? 'Back to dashboard' : 'Back to sign in'}
          </Link>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white/80 px-5 py-3 text-sm font-semibold text-slate-700 backdrop-blur-sm transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:ring-offset-2"
          >
            Go back
          </button>
        </motion.div>

        <motion.p
          variants={item}
          className="mt-8 text-xs text-slate-500"
        >
          Need a hand?{' '}
          <Link to="/help" className="font-medium text-teal-700 underline-offset-2 hover:underline">
            Visit Help
          </Link>
          {' · '}
          <Link to="/contact" className="font-medium text-teal-700 underline-offset-2 hover:underline">
            Contact
          </Link>
        </motion.p>
      </motion.main>
    </div>
  );
}
